import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { fileUrl, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, Clock, ImageOff, Truck } from "lucide-react";
import { Seo } from "../lib/seo";

const MAX = 8;

export default function OrderConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const { settings } = useSettings();
  const dmin = settings?.delivery_days_min ?? 2;
  const dmax = settings?.delivery_days_max ?? 5;
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("timeout"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.order) setOrder(data.order);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (attempts.current >= MAX) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      } catch { if (attempts.current >= MAX) setStatus("timeout"); else timer = setTimeout(poll, 2000); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <Seo title="Ordine confermato | Herbalife Rossano" description="Conferma del tuo ordine." noindex />
      <Card className="p-8 rounded-3xl border-slate-200 text-center" data-testid="order-result">
        {status === "checking" && <><Clock className="w-14 h-14 text-amber-400 mx-auto animate-pulse" /><h1 className="mt-4 text-2xl font-bold font-display">Verifica pagamento...</h1></>}
        {status === "paid" && <><CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" /><h1 className="mt-4 text-3xl font-extrabold font-display">Ordine confermato!</h1><p className="mt-2 text-slate-500">Grazie per il tuo ordine.</p><p className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2" data-testid="delivery-note"><Truck className="w-4 h-4" /> Consegna prevista in {dmin}-{dmax} giorni lavorativi dal buon esito del pagamento.</p></>}
        {status === "timeout" && <><Clock className="w-14 h-14 text-amber-400 mx-auto" /><h1 className="mt-4 text-2xl font-bold font-display">Ordine in elaborazione</h1><p className="mt-2 text-slate-500">Il pagamento è in fase di verifica. Riceverai una email di conferma.</p></>}
      </Card>

      {order && (
        <Card className="mt-6 p-6 rounded-2xl border-slate-200 text-left" data-testid="order-summary">
          <div className="flex justify-between items-center">
            <span className="text-slate-500 text-sm">Numero ordine</span>
            <span className="font-bold text-slate-900" data-testid="order-number">{order.order_number}</span>
          </div>
          <div className="mt-4 space-y-3">
            {order.items.map((it, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0">
                  {fileUrl(it.image_url) ? <img src={fileUrl(it.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageOff className="w-4 h-4 text-emerald-200" />}
                </div>
                <span className="flex-1 text-slate-600">{it.name} ×{it.quantity}</span>
                <span className="font-medium">{formatPrice(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-slate-500"><span>Spedizione</span><span>{order.shipping_cost === 0 ? "Gratuita" : formatPrice(order.shipping_cost)}</span></div>
            <div className="flex justify-between font-bold text-lg"><span>Totale pagato</span><span>{formatPrice(order.total)}</span></div>
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3 text-sm text-slate-500">
            <p className="font-semibold text-slate-700 mb-1">Spedizione a:</p>
            {order.customer.nome} {order.customer.cognome}<br />
            {order.shipping.indirizzo} {order.shipping.civico}, {order.shipping.cap} {order.shipping.comune} ({order.shipping.provincia})
          </div>
        </Card>
      )}

      <div className="mt-6 text-center"><Link to="/catalogo"><Button className="rounded-full px-8" data-testid="back-to-catalog">Torna al catalogo</Button></Link></div>
    </div>
  );
}
